import type { BodyHandle } from "./types";
import { GRID_CELL_M, pairKey } from "./types";

export class SpatialGrid {
  private cells = new Map<number, BodyHandle[]>();
  private cellPool: BodyHandle[][] = [];

  private key(cx: number, cy: number): number {
    return ((cx & 0xffff) << 16) | (cy & 0xffff);
  }

  /** Insert under every cell the swept AABB touches (anti-tunnel). */
  insert(b: BodyHandle): void {
    const r = b.radius;
    const minX = (b.prevPos.x < b.pos.x ? b.prevPos.x : b.pos.x) - r;
    const maxX = (b.prevPos.x > b.pos.x ? b.prevPos.x : b.pos.x) + r;
    const minY = (b.prevPos.y < b.pos.y ? b.prevPos.y : b.pos.y) - r;
    const maxY = (b.prevPos.y > b.pos.y ? b.prevPos.y : b.pos.y) + r;
    const x0 = Math.floor(minX / GRID_CELL_M);
    const x1 = Math.floor(maxX / GRID_CELL_M);
    const y0 = Math.floor(minY / GRID_CELL_M);
    const y1 = Math.floor(maxY / GRID_CELL_M);
    for (let x = x0; x <= x1; x++) {
      for (let y = y0; y <= y1; y++) {
        const k = this.key(x, y);
        const arr = this.cells.get(k);
        if (arr) {
          arr.push(b);
        } else {
          const fresh = this.cellPool.pop() ?? [];
          fresh.push(b);
          this.cells.set(k, fresh);
        }
      }
    }
  }

  /** Writes pair endpoints into parallel arrays — `outA[i]` pairs with `outB[i]`. */
  collectPairs(seen: Set<number>, outA: BodyHandle[], outB: BodyHandle[]): void {
    seen.clear();
    outA.length = 0;
    outB.length = 0;
    for (const arr of this.cells.values()) {
      const n = arr.length;
      for (let i = 0; i < n; i++) {
        const a = arr[i];
        for (let j = i + 1; j < n; j++) {
          const b = arr[j];
          if (a.id === b.id) continue;
          if (a.noSelfContact && b.noSelfContact && a.group === b.group) continue;
          if (a.owner === b.id || b.owner === a.id) continue;
          if (a.connected && a.connected.has(b.id)) continue;
          if (b.connected && b.connected.has(a.id)) continue;
          const k = pairKey(a.idx, b.idx);
          if (seen.has(k)) continue;
          seen.add(k);
          // Swept AABB; single-pos check would let tunneling slip past CCD.
          const aMinX = (a.prevPos.x < a.pos.x ? a.prevPos.x : a.pos.x) - a.radius;
          const aMaxX = (a.prevPos.x > a.pos.x ? a.prevPos.x : a.pos.x) + a.radius;
          const aMinY = (a.prevPos.y < a.pos.y ? a.prevPos.y : a.pos.y) - a.radius;
          const aMaxY = (a.prevPos.y > a.pos.y ? a.prevPos.y : a.pos.y) + a.radius;
          const bMinX = (b.prevPos.x < b.pos.x ? b.prevPos.x : b.pos.x) - b.radius;
          const bMaxX = (b.prevPos.x > b.pos.x ? b.prevPos.x : b.pos.x) + b.radius;
          const bMinY = (b.prevPos.y < b.pos.y ? b.prevPos.y : b.pos.y) - b.radius;
          const bMaxY = (b.prevPos.y > b.pos.y ? b.prevPos.y : b.pos.y) + b.radius;
          if (aMaxX < bMinX || aMinX > bMaxX) continue;
          if (aMaxY < bMinY || aMinY > bMaxY) continue;
          outA.push(a);
          outB.push(b);
        }
      }
    }
  }

  clear(): void {
    for (const arr of this.cells.values()) {
      arr.length = 0;
      this.cellPool.push(arr);
    }
    this.cells.clear();
  }
}
